import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { faCheck, faChevronDown } from "@fortawesome/pro-regular-svg-icons";

import { classNames } from "@/utils/classNames";
import { useAppDispatch } from "@/redux/hooks";

import { Icon } from "@/components";

import { openTour } from "@/pages/Private/redux/onboardingTour/onboardingTour.slice";
import { TourStepId } from "@/pages/Private/redux/onboardingTour/schema";

import { UnipileConnectType } from "@/pages/Private/pages/Account/hooks/useUnipileConnect";

import { FirstStepItem, OnboardingItemPill } from "../utils/firstStepsToItems";
import { LEARN_CONTENT } from "../constants/learnContent";
import { ConnectAccountFlow } from "./ConnectAccountFlow";
import { LearnMoreModal } from "./LearnMoreModal";

/** localStorage key persisting the collapsed state of the checklist card. */
export const ONBOARDING_BOX_COLLAPSED_KEY = "dashboardOnboardingCollapsed";

interface OnboardingBoxProps {
	items: FirstStepItem[];
}

const ProgressRing: FunctionComponent<{ percent: number }> = ({ percent }) => {
	const radius = 14;
	const circumference = 2 * Math.PI * radius;
	const offset = circumference * (1 - percent / 100);

	return (
		<div className="relative w-9 h-9 shrink-0">
			<svg className="w-9 h-9 -rotate-90" viewBox="0 0 36 36">
				<circle
					className="text-border-light"
					cx="18"
					cy="18"
					fill="none"
					r={radius}
					stroke="currentColor"
					strokeWidth="3"
				/>
				<circle
					className="text-brand-light transition-[stroke-dashoffset] duration-700 ease-out"
					cx="18"
					cy="18"
					fill="none"
					r={radius}
					stroke="currentColor"
					strokeDasharray={circumference}
					strokeDashoffset={offset}
					strokeLinecap="round"
					strokeWidth="3"
				/>
			</svg>
			<div className="absolute inset-0 flex items-center justify-center text-[10px] font-semibold text-text">
				{percent}%
			</div>
		</div>
	);
};

const RowPill: FunctionComponent<{ pill: OnboardingItemPill }> = ({ pill }) => {
	const { t } = useTranslation();

	return (
		<span
			className={classNames(
				"text-[10px] font-semibold rounded-full px-2 py-0.5 shrink-0",
				pill === "resultReady"
					? "bg-status-success-light text-status-success"
					: "bg-background-light text-text-meta"
			)}
		>
			{t(`onboardingTour.dashboard.pills.${pill}`)}
		</span>
	);
};

const CTA_CLASSES =
	"text-xs font-medium text-white bg-blue-action hover:shadow-md rounded px-2.5 py-1 cursor-pointer";

const OnboardingRow: FunctionComponent<{
	item: FirstStepItem;
	onLearnMore: (item: FirstStepItem) => void;
	onConnect: (type: UnipileConnectType) => void;
	onOpenTour: (step: TourStepId) => void;
}> = ({ item, onLearnMore, onConnect, onOpenTour }) => {
	const { t } = useTranslation();
	const hasLearnTopic = Boolean(item.learnTopic && LEARN_CONTENT[item.learnTopic]);

	return (
		<div className="flex items-center gap-3 px-4 py-2.5" data-testid={`onboarding-row-${item.key}`}>
			<div
				className={classNames(
					"w-5 h-5 rounded-full flex items-center justify-center shrink-0 border",
					item.done
						? "bg-status-success border-status-success text-white"
						: "bg-white border-border-strong"
				)}
			>
				{item.done && <Icon className="w-3 h-3" icon={faCheck} />}
			</div>
			<div className="flex-1 min-w-0 flex items-center gap-2">
				<div className={classNames("text-sm truncate", item.done ? "text-text-meta" : "text-text")}>
					{item.title}
				</div>
				{item.pill && <RowPill pill={item.pill} />}
			</div>
			<div className="flex items-center gap-2 shrink-0">
				{!item.done &&
					(item.tourStep ? (
						<button
							className={CTA_CLASSES}
							type="button"
							onClick={() => onOpenTour(item.tourStep as TourStepId)}
						>
							{item.actionLabel}
						</button>
					) : item.connect ? (
						<button
							className={CTA_CLASSES}
							type="button"
							onClick={() => onConnect(item.connect as UnipileConnectType)}
						>
							{item.actionLabel}
						</button>
					) : (
						<Link className={CTA_CLASSES} to={item.to}>
							{item.actionLabel}
						</Link>
					))}
				{hasLearnTopic && (
					<button
						className="text-xs font-medium text-text-meta border border-border-strong hover:bg-background-light rounded px-2.5 py-1 cursor-pointer"
						type="button"
						onClick={() => onLearnMore(item)}
					>
						{t("onboardingTour.dashboard.learnMore")}
					</button>
				)}
			</div>
		</div>
	);
};

export const OnboardingBox: FunctionComponent<OnboardingBoxProps> = ({ items }) => {
	const { t } = useTranslation();
	const dispatch = useAppDispatch();
	const [open, setOpen] = useState(
		() => localStorage.getItem(ONBOARDING_BOX_COLLAPSED_KEY) !== "1"
	);
	const [learnItem, setLearnItem] = useState<FirstStepItem | null>(null);
	const [connectType, setConnectType] = useState<UnipileConnectType | null>(null);

	const done = items.filter((item) => item.done).length;
	const total = items.length;
	const percent = total > 0 ? Math.round((done / total) * 100) : 0;
	const remaining = total - done;

	if (remaining === 0) {
		return null;
	}

	const toggleOpen = () =>
		setOpen((wasOpen) => {
			localStorage.setItem(ONBOARDING_BOX_COLLAPSED_KEY, wasOpen ? "1" : "0");

			return !wasOpen;
		});

	const openTourAtStep = (step: TourStepId) => dispatch(openTour(step));

	return (
		<div className="bg-white border border-border-light rounded-xl overflow-hidden">
			<button
				className="w-full flex items-center gap-3 px-4 py-3 cursor-pointer text-left hover:bg-background-light"
				type="button"
				onClick={toggleOpen}
			>
				<ProgressRing percent={percent} />
				<div className="flex-1 min-w-0">
					<div className="text-sm font-semibold text-text">
						{t("onboardingTour.dashboard.title")}
					</div>
					<div className="text-xs text-text-meta">
						{t("onboardingTour.dashboard.progress", { done, total, remaining })}
					</div>
				</div>
				<Icon
					className={classNames(
						"w-4 h-4 text-text-meta transition-transform",
						open ? "" : "-rotate-90"
					)}
					icon={faChevronDown}
				/>
			</button>
			{open && (
				<div className="divide-y divide-divider border-t border-divider">
					{items.map((item) => (
						<OnboardingRow
							key={item.key}
							item={item}
							onConnect={setConnectType}
							onLearnMore={setLearnItem}
							onOpenTour={openTourAtStep}
						/>
					))}
				</div>
			)}
			<LearnMoreModal
				item={learnItem}
				onClose={() => setLearnItem(null)}
				onConnect={setConnectType}
			/>
			<ConnectAccountFlow onClose={() => setConnectType(null)} type={connectType} />
		</div>
	);
};
